import { sortAsc } from '@platform/shared/utilities/sort-utility';
import { Component, DestroyRef, HostListener, Inject, OnInit, effect, signal } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, combineLatest } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { map, startWith, tap } from 'rxjs/operators';
import { Role, RoleName, User, getUserDisplayName } from '@core/core.state';
import { RootState } from '@root/app.state';
import * as PlatformActions from '@platform/platform.actions';
import { go } from '@platform/router.actions';
import { requireSelection } from '@platform/shared/utilities/require-selection-validator';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { selectNavigatedCustomerSpaceName } from '@core/core.selectors';
import { OptionsStoreService, UiSingleOptions } from '../exper-table/sort-options-store.service';

@Component({
  selector: 'app-update-user-role-dialog',
  templateUrl: './update-user-role-dialog.component.html',
  styleUrls: ['./update-user-role-dialog.component.scss'],
})
export class UpdateUserRoleDialogComponent implements OnInit {
  public updateUserForm!: UntypedFormGroup;

  public filteredUsers$!: Observable<User[]>;
  public accessibleRoles: Role[] = [];
  public isEditMode: boolean;
  public userId: string | null = null;

  randomName1 = Math.random().toString();
  filterUsersByCustomerSpace = signal(
    this.optionsStore.getOptionOrDefault(UiSingleOptions.FpaManageAccessUserRoleDialogFilterUsersByCustomerSpace, true),
  );

  private availableRoles: Role[];
  private activeUsers: User[];
  private customerSpaceName = '';

  constructor(
    private dialogRef: MatDialogRef<UpdateUserRoleDialogComponent>,
    private store$: Store<RootState>,
    private formBuilder: UntypedFormBuilder,
    private translate: TranslateService,
    private optionsStore: OptionsStoreService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private destroyRef: DestroyRef,
  ) {
    this.activeUsers = data.activeUsers;
    this.availableRoles = data.availableRoles;
    this.isEditMode = !!data.selectedUser;

    this.setupUserForm(data.selectedUser, data.selectedRoleId);

    effect(() => {
      this.optionsStore.setOption(
        UiSingleOptions.FpaManageAccessUserRoleDialogFilterUsersByCustomerSpace,
        this.filterUsersByCustomerSpace(),
      );
    });
  }

  public get sortedAccessibleRoles(): Role[] {
    return this.accessibleRoles.sort((a, b) => sortAsc(a, b, (x) => this.getTranslatedRoleName(x.name)));
  }

  @HostListener('document:keydown.enter', ['$event'])
  enter() {
    this.saveDialog();
  }

  ngOnInit(): void {
    this.store$
      .select(selectNavigatedCustomerSpaceName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((name) => (this.customerSpaceName = name));
  }

  public saveDialog() {
    if (!this.user().value) {
      this.closeDialog();
      return;
    } else if (!this.updateUserForm.valid) {
      return;
    }
    this.store$.dispatch(
      PlatformActions.closeDialog({
        id: this.dialogRef.id,
        data: {
          selectedUser: this.user().value,
          selectedRoleId: this.roleId().value,
        },
      }),
    );
  }

  public closeDialog(): void {
    this.store$.dispatch(PlatformActions.closeDialog({ id: this.dialogRef.id }));
  }

  public getTranslatedRoleName(name: RoleName): string {
    if (!name) {
      return null!;
    }

    const translatedRoleName: string = this.translate.instant(`roles.${name.toLocaleLowerCase()}`);
    return translatedRoleName.startsWith('roles.') ? name : translatedRoleName;
  }

  public getUserDisplayName(user: User): string {
    return getUserDisplayName(user);
  }

  public setSelectedUser(user: User) {
    if (!user) {
      this.roleId().reset();
      this.roleId().disable();
      return;
    }

    this.accessibleRoles = this.availableRoles.filter((r) => !r.isRestrictedToInternalUsers || user.isInternal);
    this.roleId().reset();
    this.roleId().enable();
  }

  public goToUserProfile(): void {
    if (!this.userId) {
      return;
    }

    this.closeDialog();
    this.store$.dispatch(go({ path: ['/', 'users', this.userId] }));
  }

  private setupUserForm(selectedUser: User, selectedRoleId: string) {
    this.updateUserForm = this.formBuilder.group({
      user: [selectedUser, [Validators.required, requireSelection]],
      roleId: [selectedRoleId, [Validators.required]],
    });

    if (!!selectedUser) {
      this.userId = selectedUser.id;
      this.user().disable();
      this.accessibleRoles = this.availableRoles.filter((r) => !r.isRestrictedToInternalUsers || selectedUser.isInternal);
    } else {
      this.userId = null;
      this.roleId().disable();
    }

    this.filteredUsers$ = combineLatest([toObservable(this.filterUsersByCustomerSpace), this.user().valueChanges.pipe(startWith(''))]).pipe(
      map(([filterByCustomerSpace, val]) =>
        this.filterUsers(filterByCustomerSpace, val).sort((a, b) => sortAsc(a, b, (x) => getUserDisplayName(x))),
      ),
    );
  }

  private user(): AbstractControl {
    return this.updateUserForm.get('user')!;
  }

  private roleId(): AbstractControl {
    return this.updateUserForm.get('roleId')!;
  }

  private filterUsers(filterByCustomerSpace: boolean, val: string): User[] {
    let users = this.activeUsers;
    if (filterByCustomerSpace && this.customerSpaceName) {
      const customerSpaceName = this.customerSpaceName.toLowerCase();
      users = users.filter((u) => u.isInternal || u.organizationUnit?.toLowerCase() === customerSpaceName);
    }

    if (val && val.length > 0) {
      users = users.filter((u) => this.getUserDisplayName(u).toLowerCase().includes(val.toLowerCase()));
    }

    return users;
  }
}
